// Conversation mappers - transform backend-specific conversation data to contract types
import type { Conversation, Message } from '@/types/domain'
import type { ConversationsListResponse, SingleConversationResponse, ConversationMessagesResponse, SuccessMessageResponse } from '@/types/api'
import { mapMessageFromSupabase } from './messages'
import { sanitizeConversationPreview } from '@/utils/conversationPreview'
import { normalizeDate } from './normalize'

interface RawMessage {
  id: string | number;
  conversation_id: string | number;
  [key: string]: unknown;
}

function normalizeMessageCount(value: unknown): number {
  if (value === null || value === undefined) return 0
  const normalized = Number(value)
  return Number.isFinite(normalized) ? normalized : 0
}

export function mapConversationFromSupabase(value: unknown): Conversation {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid conversation response')
  }
  const raw = value as Record<string, unknown>
  const date = (input: unknown): string => typeof input === 'string' ? normalizeDate(input) ?? '' : ''
  return {
    ...raw,
    id: Number(raw.id),
    user_id: typeof raw.user_id === 'string' ? raw.user_id : undefined,
    character_id: Number(raw.character_id),
    persona_id: Number(raw.persona_id),
    bot_id: raw.bot_id === null || raw.bot_id === undefined ? null : Number(raw.bot_id),
    message_count: normalizeMessageCount(raw.message_count),
    created_at: date(raw.created_at),
    updated_at: date(raw.updated_at),
    last_message_at: typeof raw.last_message_at === 'string' ? date(raw.last_message_at) : null,
    title: typeof raw.title === 'string' ? raw.title : '',
    last_message_preview: sanitizeConversationPreview(typeof raw.last_message_preview === 'string' ? raw.last_message_preview : null),
  }
}

export function mapConversationsFromSupabase(rawList: unknown[]): Conversation[] {
  return rawList.map(mapConversationFromSupabase)
}

export function mapConversationResponseFromSupabase(raw: { conversation: unknown }): SingleConversationResponse {
  return { conversation: mapConversationFromSupabase(raw.conversation) }
}

export function mapConversationMessagesResponseFromSupabase(raw: { messages: RawMessage[] }): ConversationMessagesResponse {
  return {
    messages: raw.messages?.map(mapMessageFromSupabase) || [],
  };
}

export function mapSuccessMessageResponseFromSupabase(raw: { message?: string }): SuccessMessageResponse {
  return {
    message: raw.message || 'Operation completed successfully',
  };
}
